import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import { AuthDialog } from "../components/forum/AuthDialog";
import { NewPostDialog } from "../components/forum/NewPostDialog";

const CATS = ["All", "For Sale", "Trade", "Wanted"];
const API_BASE = api.defaults.baseURL;

const BADGE_CLS = {
  "For Sale": "bg-primary text-white",
  Trade: "border border-white/40 text-white",
  Wanted: "bg-violet-500/15 text-violet-300 border border-violet-400/30",
};

export default function ForumPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [authOpen, setAuthOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [postAfterAuth, setPostAfterAuth] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("signin") && user === false) {
      setPostAfterAuth(false);
      setAuthOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posts", { params: category === "All" ? {} : { category } });
      setPosts(data);
    } catch {
      toast.error("Could not load posts");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Post removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleNewPost = () => {
    if (user) setPostOpen(true);
    else {
      setPostAfterAuth(true);
      setAuthOpen(true);
    }
  };

  const openSignIn = () => {
    setPostAfterAuth(false);
    setAuthOpen(true);
  };

  return (
    <div data-testid="forum-page" className="bg-[#050505] text-white min-h-screen">
      <Nav />

      <div className="px-6 md:px-12 pt-44 md:pt-52 pb-8 border-b border-white/10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-violet-400 text-xs tracking-[0.35em] uppercase mb-4">Community Board</p>
          <h1 className="font-display uppercase tracking-tighter text-5xl md:text-7xl leading-[0.95]">
            The <span className="text-outline-purple">Marketplace</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg mt-6 max-w-2xl">
            Buy, sell, and trade with folks in the community. Create an account to list
            your own items — buyers reach out to you directly.
          </p>
        </motion.div>
      </div>

      <div className="px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4 border-b border-white/10">
        <div className="flex flex-wrap gap-2" data-testid="forum-category-tabs">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`forum-tab-${c.toLowerCase().replace(" ", "-")}`}
              onClick={() => setCategory(c)}
              className={`px-5 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                category === c ? "bg-primary border-primary text-white" : "border-white/15 text-white/50 hover:text-white hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <button
          data-testid="new-post-btn"
          onClick={handleNewPost}
          className="bg-primary text-white px-6 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-[#FF7733] transition-colors flex items-center gap-2"
        >
          <Plus weight="bold" /> New Listing
        </button>
      </div>

      {user === false && (
        <div data-testid="forum-guest-banner" className="px-6 md:px-12 py-4 border-b border-white/10 text-sm text-white/50">
          Browsing as a guest.{" "}
          <button data-testid="forum-guest-signin-btn" onClick={openSignIn} className="text-primary hover:text-[#FF7733] uppercase tracking-widest text-xs font-semibold transition-colors">
            Sign in
          </button>{" "}
          to post your own listings.
        </div>
      )}

      <div className="px-6 md:px-12 pb-24">
        {loading ? (
          <p data-testid="forum-loading" className="py-20 text-white/40 uppercase tracking-widest text-sm">Loading listings...</p>
        ) : posts.length === 0 ? (
          <div data-testid="forum-empty" className="py-20 border-b border-white/10">
            <p className="font-display uppercase text-3xl text-white/30">Nothing listed here yet</p>
            <p className="text-white/50 mt-3 text-sm">Be the first — post an item for sale or trade.</p>
          </div>
        ) : (
          posts.map((post, i) => (
            <motion.div
              key={post.id}
              data-testid={`forum-post-${post.id}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.5 }}
              className="grid md:grid-cols-12 gap-4 border-b border-white/10 py-8 items-start"
            >
              <div className="md:col-span-6">
                <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-widest ${BADGE_CLS[post.category] || BADGE_CLS.Wanted}`}>
                  {post.category}
                </span>
                <Link to={`/forum/post/${post.id}`} data-testid={`forum-post-link-${post.id}`}>
                  <h3 className="font-display uppercase text-2xl md:text-3xl mt-3 tracking-tight hover:text-primary transition-colors">{post.title}</h3>
                </Link>
                <p className="text-white/60 text-sm mt-3 max-w-xl whitespace-pre-line leading-relaxed">{post.description}</p>
                {post.images && post.images.length > 0 && (
                  <div data-testid={`forum-post-images-${post.id}`} className="flex gap-3 mt-4 flex-wrap">
                    {post.images.map((p, idx) => (
                      <a key={p} href={`${API_BASE}/files/${p}`} target="_blank" rel="noopener noreferrer">
                        <img
                          src={`${API_BASE}/files/${p}`}
                          alt={`${post.title} photo ${idx + 1}`}
                          loading="lazy"
                          className="w-24 h-24 object-cover border border-white/10 hover:border-primary transition-colors"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="md:col-span-2 text-primary font-semibold text-lg" data-testid={`forum-post-price-${post.id}`}>
                {post.price || "—"}
              </div>
              <div className="md:col-span-2 text-sm text-white/50">
                <p className="text-white/80">{post.author_name}</p>
                <p className="mt-1">{format(new Date(post.created_at), "MMM d, yyyy")}</p>
                {post.contact && <p className="mt-2 text-white/60 break-all">{post.contact}</p>}
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                {user && (user.id === post.author_id || user.role === "admin") && (
                  <button
                    data-testid={`forum-post-delete-${post.id}`}
                    onClick={() => remove(post.id)}
                    className="text-white/30 hover:text-red-400 transition-colors flex items-center gap-2 text-xs uppercase tracking-widest"
                  >
                    <Trash size={16} /> Remove
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onAuthed={() => postAfterAuth && setPostOpen(true)} />
      <NewPostDialog open={postOpen} onOpenChange={setPostOpen} onCreated={load} />
    </div>
  );
}
